import { filter, flatten, isNil, map, pipe, transpose } from 'ramda';

/*
 * Custom grid tab order functionality
 */

/**
 * Create an event handler that handles custom tab ordering.
 *
 * Components in the grid must have props `focusRef` and `onKeyDown` so we can get at the HTML Element to focus on, and
 * listen for tab/enter key presses.
 *
 * @param existingHandler
 * @param transposeTabOrder
 * @param focusRefs
 */
export const handleKeyDownWithGridTabbing =
  (
    existingHandler: ((event: React.KeyboardEvent<HTMLElement>) => void) | undefined,
    transposeTabOrder: boolean,
    focusRefs: (React.RefObject<HTMLElement> | null | undefined)[][] | undefined,
    thisInput: React.RefObject<HTMLElement> | null | undefined,
    inputSupportsEnterAsTab?: boolean
  ) =>
  (event: React.KeyboardEvent<HTMLElement>) => {
    // Run custom key down handlers
    existingHandler?.(event);

    // Skip tabbing sometimes
    if (event.defaultPrevented) return;
    // Enter key behaviour was removed in QP-5552. This will be re-investigated as part of QP-5482
    if (event.key !== 'Tab' && !(event.key === 'Enter' && inputSupportsEnterAsTab)) return;
    if (!focusRefs) return;

    // Create flattened list of valid inputs for focusing on
    const inputList = transposeTabOrder ? toTransposedInputList(focusRefs) : toInputList(focusRefs);

    // Find which input we're on now
    const index = inputList.findIndex((i) => thisInput?.current && i === thisInput?.current);

    const trapEvent = () => {
      // Don't change without testing you can select dropdown elements in a grid with 'enter'.
      if (event.key === 'Enter' && inputSupportsEnterAsTab) event.stopPropagation();
      if (event.key === 'Tab') event.preventDefault();
    };

    // Set focus on the next valid input
    if (!event.shiftKey && index >= 0 && index < inputList.length - 1) {
      trapEvent();
      inputList[index + 1].focus();
      return;
    } else if (event.shiftKey && index > 0) {
      trapEvent();
      inputList[index - 1].focus();
      return;
    }
  };

// Create a flat list of the inputs
const toInputList = (focusRefs: (React.RefObject<HTMLElement> | null | undefined)[][]) =>
  pipe(
    (arg: typeof focusRefs) => flatten(arg),
    map((field) => field?.current),
    filter<HTMLElement | null | undefined>((input: any) => !isNil(input) && !!input.focus && !input.disabled)
  )(focusRefs) as HTMLElement[];

// Create a transposed flat list of the inputs (to tranpsose tab order)
const toTransposedInputList = (focusRefs: (React.RefObject<HTMLElement> | null | undefined)[][]) =>
  pipe((arg: typeof focusRefs) => transpose(arg), toInputList)(focusRefs);
